import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import * as L from 'leaflet';
import { ICompany } from '../../../core/models/ICompany';
import { CompanyService } from '../../../core/services/company.service';
import { environment } from '../../../../environments/environment';
import { LogService } from '../../../core/services/log.service';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [],
  templateUrl: './map.html',
  styleUrl: './map.scss'
})
export class Map implements AfterViewInit, OnDestroy {
  private map?: L.Map;
  private markers: L.Marker[] = [];
  loading = true;
  error = '';
  address = '';

  constructor(private companyService: CompanyService, private logger: LogService) {}

  ngAfterViewInit() {setTimeout(() => this.initializeMap(), 0)}
  ngOnDestroy() {this.map?.remove()}

  private async initializeMap() {
    try {
      // Centrée sur Bruxelles par défaut
      this.map = L.map('map', {
        center: [50.8503, 4.3517],
        zoom: 12
      });

      // Ajout tiles OpenStreetMap avec configuration complète
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        minZoom: 1,
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        crossOrigin: true
      }).addTo(this.map);

      // Forcer un refresh de la carte après chargement
      setTimeout(() => {
        this.map?.invalidateSize();
      }, 100);

      // Géolocalisation de l'utilisateur
      this.getUserLocation();

      // Charger les companies depuis le backend
      this.companyService.getAllCompanies().subscribe((companies) => {
        if (companies && companies.length > 0) {
          this.addCompanyMarkers(companies);
        }
        this.loading = false;
      });

    } catch (err) {
      this.error = 'Erreur lors du chargement de la carte';
      this.loading = false;
      this.logger.error('Erreur carte: ' + JSON.stringify(err));
    }
  }

  private getUserLocation() {
    if (!this.map) return;

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;

          this.map?.setView([userLat, userLng], 11);

          const url = `https://nominatim.openstreetmap.org/reverse?lat=${userLat}&lon=${userLng}&format=json&addressdetails=1`;
          try {
            const response = await fetch(url, {
              headers: {
                'User-Agent': 'Referio/1.0'
              }
            });
            const data = await response.json();
            this.address = data.display_name;
            this.logger.log("Adresse trouvée: " + this.address);
          } catch (err) {
            this.logger.error("Erreur reverse geocoding: " + JSON.stringify(err));
          }


          // Ajouter un marqueur pour la position de l'utilisateur
          const userIcon = L.divIcon({
            className: 'user-location-marker',
            html: `<i class="bi bi-crosshair2" style="font-size: 25px; color: #c60000;"></i>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          });

          L.marker([userLat, userLng], { icon: userIcon })
            .bindPopup(`Votre position<hr/>${this.address}`)
            .addTo(this.map!);

          // Ajouter un cercle de 20km autour de l'utilisateur (optionnel)
          L.circle([userLat, userLng], {
            radius: 20000, // 20km en mètres
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.1,
            weight: 2
          }).addTo(this.map!);

        },
        (error) => {
          this.logger.warn('Géolocalisation refusée ou impossible: ' + JSON.stringify(error));
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    }
  }

  private addCompanyMarkers(companies: ICompany[]) {
    if (!this.map) return;

    companies.forEach(company => {
      // GeoJSON utilise [lng, lat], Leaflet utilise [lat, lng]
      const [lng, lat] = company.location.coordinates;

      // Créer un marqueur personnalisé (optionnel)
      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `<i class="bi bi-geo-alt-fill" style="font-size: 28px; color: blue;"></i>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });

      // Créer le popup
      const popupContent = `
        <div class="company-popup">
          <div class="company-header">
            <img
              src="${company.logo ? environment.apiUrl + company.logo : '/images/logo/Referio.png'}"
              alt="${company.companyName}"
              class="company-logo"
              onerror="this.style.display='none'"
              height="50"
            />

            <h3>${company.companyName}</h3>
          </div>

          ${company.address ? `
            <p>${company.address.street} ${company.address.number}</p>
            <p>${company.address.zipCode} ${company.address.city}</p>
          ` : ''}

          <a href="/company/${company._id}" class="btn btn-outline-primary btn-sm">
              <i class="bi bi-eye me-1"></i> Voir les détails
          </a>
        </div>
      `;

      // Ajouter le marqueur
      const marker = L.marker([lat, lng], { icon: customIcon })
        .bindPopup(popupContent, { className: 'custom-popup' })
        .addTo(this.map!);

      this.markers.push(marker);
    });
  }
}